import GenericModal from 'components/app-components/modals/GenericModal';
import Button from 'components/app-components/ui/button';
import { useState } from 'react';

// Bank disclosure links
const DISCLOSURE_LINKS = {
  bridgePrivacyPolicy: 'https://www.bridge.xyz/legal#row-privacy-policy',
  bridgeTermsOfService: 'https://www.bridge.xyz/legal',
  leadPrivacyPolicy: 'https://www.lead.bank/privacy-and-terms',
  eSignConsent: 'https://www.bridge.xyz/legal/e-sign-consent-disclosure',
  cardholderAgreement: 'https://www.bridge.xyz/card-agreements/thera-card',
} as const;

interface BankDisclosuresModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  isLoading?: boolean;
}

/**
 * Bank Disclosures Modal component that displays required bank disclosures
 * before allowing users to create a Thera Card
 */
const BankDisclosuresModal: React.FC<BankDisclosuresModalProps> = ({ open, onClose, onAccept, isLoading = false }) => {
  const [isAgreementAccepted, setIsAgreementAccepted] = useState(false);

  const handleButtonClick = () => {
    // Single step: User checked checkbox and clicked Accept
    onAccept();
  };

  const onModalClose = () => {
    setIsAgreementAccepted(false);
    onClose();
  };

  return (
    <GenericModal
      open={open}
      onClose={onModalClose}
      preventOutsideClick={isLoading}
      title="Bank Disclosures"
      titleCenter
      showHeaderBorder={false}
    >
      <div className="max-w-md">
        {/* Main Content */}
        <div className="mb-6">
          <p className="text-center text-sm text-neutral-600">
            Please review the following required bank disclosures before proceeding.
          </p>
        </div>

        {/* Disclosure Links Section */}
        <div className="mb-6 flex items-start gap-3">
          <input
            type="checkbox"
            id="consent-checkbox"
            checked={isAgreementAccepted}
            onChange={(e) => setIsAgreementAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 flex-shrink-0 rounded border-neutral-300 text-therablue focus:ring-therablue"
          />
          <label htmlFor="consent-checkbox" className="text-sm text-neutral-900">
            I consent to use electronic signatures for documents and agreements related to the Bridge Card Program, and
            I agree to the{' '}
            <a
              href={DISCLOSURE_LINKS.eSignConsent}
              className="text-therablue underline hover:text-blue-800"
              target="_blank"
              rel="noopener noreferrer"
            >
              E-Sign Consent
            </a>
            ,{' '}
            <a
              href={DISCLOSURE_LINKS.cardholderAgreement}
              className="text-therablue underline hover:text-blue-800"
              target="_blank"
              rel="noopener noreferrer"
            >
              Lead Bank Cardholder Agreement
            </a>
            ,{' '}
            <a
              href={DISCLOSURE_LINKS.bridgeTermsOfService}
              className="text-therablue underline hover:text-blue-800"
              target="_blank"
              rel="noopener noreferrer"
            >
              Bridge Terms of Service
            </a>
            ,{' '}
            <a
              href={DISCLOSURE_LINKS.bridgePrivacyPolicy}
              className="text-therablue underline hover:text-blue-800"
              target="_blank"
              rel="noopener noreferrer"
            >
              Bridge Privacy Policy
            </a>
            , and{' '}
            <a
              href={DISCLOSURE_LINKS.leadPrivacyPolicy}
              className="text-therablue underline hover:text-blue-800"
              target="_blank"
              rel="noopener noreferrer"
            >
              Lead Bank Privacy Policy
            </a>
            .
          </label>
        </div>

        {/* Action Button */}
        <div className="flex justify-end">
          <Button onClick={handleButtonClick} disabled={isLoading || !isAgreementAccepted} loading={isLoading}>
            Accept
          </Button>
        </div>
      </div>
    </GenericModal>
  );
};

export default BankDisclosuresModal;
